<template>
  <div class="cost-management-chart rounded-lg border border-gray-200 p-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <div>
        <h3 class="text-lg font-semibold text-gray-900">
          {{ $t('COST_MANAGEMENT') }}
        </h3>
        <p class="text-sm text-gray-500">
          {{ $t('FUEL_AND_MAINTENANCE_COSTS') }}
        </p>
      </div>
    </div>

    <!-- Chart -->
    <div class="chart-container">
      <apexchart
        ref="chart"
        v-if="chartOptions && chartSeries.length"
        type="donut"
        height="290"
        :options="chartOptions"
        :series="chartSeries"
      />
      <div v-else class="flex items-center justify-center h-64 text-gray-500">
        {{ $t('NO_DATA') }}
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'

export default {
  name: 'CostManagementChart',
  components: { apexchart: VueApexCharts },
  props: {
    // expects: { fuel: number, maintenance: number, fine: number }
    data: { type: Object, default: () => ({}) },
  },
  computed: {
    chartSeries() {
      if (
        !this.data ||
        (!this.data.fuel && !this.data.maintenance && !this.data.fine)
      )
        return []

      // Convert from Rial to Toman (1 Toman = 10 Rials)
      const fuelCost = (this.data.fuel || 0) / 10
      const maintenanceCost = (this.data.maintenance || 0) / 10
      const fineCost = (this.data.fine || 0) / 10

      return [fuelCost, maintenanceCost, fineCost]
    },
    chartLabels() {
      return [
        this.$t('FUEL_COSTS'),
        this.$t('MAINTENANCE_COSTS'),
        this.$t('FINES'),
      ]
    },
    totalCosts() {
      // Convert from Rial to Toman (1 Toman = 10 Rials)
      const fuelCost = (this.data.fuel || 0) / 10
      const maintenanceCost = (this.data.maintenance || 0) / 10
      const fineCost = (this.data.fine || 0) / 10
      return fuelCost + maintenanceCost + fineCost
    },
    formattedTotal() {
      return Number(this.totalCosts).toLocaleString('fa-IR')
    },
    chartOptions() {
      if (
        !this.data ||
        (!this.data.fuel && !this.data.maintenance && !this.data.fine)
      )
        return null

      var self = this
      return {
        chart: {
          type: 'donut',
          height: 320,
          toolbar: { show: false },
          animations: { enabled: true, easing: 'easeinout', speed: 500 },
        },
        labels: self.chartLabels,
        colors: ['#DC2626', '#1E40AF', '#F59E0B'],
        plotOptions: {
          pie: {
            donut: {
              size: '65%',
              labels: {
                show: true,
                name: {
                  show: true,
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#6B7280',
                  offsetY: -15,
                  formatter: function () {
                    return self.$t('TOTAL_COSTS')
                  },
                },
                value: {
                  show: true,
                  fontSize: '15px',
                  fontWeight: 600,
                  color: '#374151',
                  offsetY: 15,
                  formatter: function (val) {
                    return self.formattedTotal
                  },
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: self.$t('TOTAL_COSTS'),
                  fontSize: '12px',
                  fontWeight: 400,
                  color: '#6B7280',
                  formatter: function (w) {
                    return self.formattedTotal
                  },
                },
              },
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            return val.toFixed(1) + '%'
          },
          style: {
            fontSize: '11px',
            fontWeight: 700,
            colors: ['#ffffff'],
          },
          dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 1,
            color: '#000',
            opacity: 0.3,
          },
        },
        legend: {
          show: true,
          position: 'bottom',
          horizontalAlign: 'left',
          floating: false,
          offsetY: 0,
          fontSize: '10px',
          labels: {
            colors: '#374151',
            useSeriesColors: false,
          },
          markers: {
            width: 14,
            height: 14,
            radius: 7,
            offsetX: 0,
            offsetY: 5,
          },
          itemMargin: {
            horizontal: 5,
            vertical: 0,
          },
        },
        tooltip: {
          enabled: true,
          theme: 'light',
          style: {
            fontSize: '13px',
            fontFamily: 'inherit',
          },
          y: {
            formatter: function (val) {
              const percentage = ((val / self.totalCosts) * 100).toFixed(1)
              return (
                Number(val).toLocaleString('fa-IR') +
                ' ' +
                self.$t('CURRENCY_UNIT') +
                ' (' +
                percentage +
                '%)'
              )
            },
          },
          fillSeriesColor: false,
        },
        responsive: [
          {
            breakpoint: 768,
            options: {
              chart: {
                height: 250,
              },
              legend: {
                position: 'bottom',
              },
            },
          },
        ],
      }
    },
  },
}
</script>

<style scoped>
.chart-container {
  width: 100%;
}
.chart-container >>> .apexcharts-legend-text {
  font-family: inherit !important;
}
.chart-container >>> .apexcharts-datalabel-label,
.chart-container >>> .apexcharts-datalabel-value {
  font-family: inherit !important;
}
</style>
